import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface LLMSession {
  id: string;
  chat_id: string;
  hostname: string;
  user_name: string | null;
  model: string | null;
  message_count: number;
  total_tokens: number;
  total_duration_ms: number;
  started_at: string;
  last_activity_at: string;
}

export interface LLMInvocation {
  id: string;
  invocation_id: string;
  session_id: string | null;
  chat_id: string;
  hostname: string;
  user_name: string | null;
  model: string | null;
  user_prompt: string | null;
  final_response: string | null;
  tool_calls_count: number;
  steps_count: number;
  prompt_tokens: number;
  completion_tokens: number;
  total_tokens: number;
  duration_ms: number;
  status: string;
  error_message: string | null;
  started_at: string;
  completed_at: string | null;
}

export interface LLMStep {
  id: string;
  invocation_id: string;
  step_index: number;
  step_type: string;
  role: string | null;
  content: string | null;
  tool_name: string | null;
  tool_arguments: any;
  tool_output: string | null;
  tokens: number | null;
  duration_ms: number | null;
  step_time: string;
}

export function useLLMSessions(limit = 100) {
  const [sessions, setSessions] = useState<LLMSession[]>([]);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from('llm_sessions')
      .select('*')
      .order('last_activity_at', { ascending: false })
      .limit(limit);
    setSessions((data as any) || []);
    setLoading(false);
  }, [limit]);

  useEffect(() => { refetch(); }, [refetch]);
  return { sessions, loading, refetch };
}

export function useLLMInvocations(filters?: { sessionId?: string; chatId?: string; limit?: number; search?: string }) {
  const [invocations, setInvocations] = useState<LLMInvocation[]>([]);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    setLoading(true);
    let q = supabase.from('llm_invocations').select('*').order('started_at', { ascending: false }).limit(filters?.limit || 200);
    if (filters?.sessionId) q = q.eq('session_id', filters.sessionId);
    if (filters?.chatId) q = q.eq('chat_id', filters.chatId);
    if (filters?.search) q = q.or(`user_prompt.ilike.%${filters.search}%,final_response.ilike.%${filters.search}%`);
    const { data } = await q;
    setInvocations((data as any) || []);
    setLoading(false);
  }, [filters?.sessionId, filters?.chatId, filters?.limit, filters?.search]);

  useEffect(() => { refetch(); }, [refetch]);
  return { invocations, loading, refetch };
}

export function useLLMSteps(invocationId: string | null) {
  const [steps, setSteps] = useState<LLMStep[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!invocationId) { setSteps([]); return; }
    setLoading(true);
    supabase
      .from('llm_steps')
      .select('*')
      .eq('invocation_id', invocationId)
      .order('step_index', { ascending: true })
      .then(({ data }) => {
        setSteps((data as any) || []);
        setLoading(false);
      });
  }, [invocationId]);

  return { steps, loading };
}